import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FilterSelect, cn, type FilterOption, type FilterOptionGroup } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { useImpersonatedBy } from '@wundo/client/impersonation.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { groupOrganisations } from '@/lib/organisations.ts';
import { useRecordOrganisationId } from '@/lib/recordOrganisation.ts';
import { useAllOrganisations } from '@/lib/useAllOrganisations.ts';
import type { OrgScope } from '@/lib/orgScope.ts';
import { useOrgScope } from '@/lib/useOrgScope.ts';

/* Organisation ids are uuids, so these never collide with one. */
const ALL = 'all';
const NOTHING = '';

const LABEL = 'Organisation';

function selectedValue(
  scope: OrgScope,
  recordId: string | null,
  showingAll: boolean,
  organisationId: string | undefined,
): string {
  if (scope === 'record') return recordId ?? NOTHING;
  if (showingAll) return ALL;
  return organisationId ?? NOTHING;
}

/**
 * The console's one organisation picker, the first filter in every page
 * header. What it offers depends on the scope the current route declares.
 */
export function GlobalOrganisationPicker({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const impersonatedBy = useImpersonatedBy();
  const superadmin = user?.role === 'superadmin';
  const { organisation, allOrganisations, setOrganisation } = useActiveOrganisation();
  const { data: organisations } = useAllOrganisations(superadmin && !impersonatedBy);
  const { scope, list } = useOrgScope();
  const recordId = useRecordOrganisationId();

  /* Impersonating pins the console to the impersonated user's organisation. */
  if (impersonatedBy || !superadmin) {
    return (
      <p className={cn('truncate text-sm font-semibold text-ink-secondary', className)}>
        {organisation?.name}
      </p>
    );
  }

  if (scope === 'none' || !organisations) {
    return (
      <FilterSelect
        hideLabel
        label={LABEL}
        className={className}
        disabled
        options={[]}
        value={NOTHING}
        placeholder={scope === 'none' ? 'Applies to all organisations' : 'Loading…'}
        onValueChange={() => {}}
      />
    );
  }

  const offersAll = scope === 'optional';

  const value = selectedValue(
    scope,
    recordId,
    offersAll && allOrganisations,
    organisation?.id,
  );

  const options: (FilterOption<string> | FilterOptionGroup<string>)[] = [
    ...(offersAll ? [{ value: ALL, label: 'All organisations' }] : []),
    ...groupOrganisations(organisations).map((group) => ({
      label: group.label,
      options: group.organisations.map(({ id, name }) => ({ value: id, label: name })),
    })),
  ];

  function choose(id: string) {
    if (id === ALL) {
      setOrganisation(null);
      return;
    }

    const next = organisations?.find((one) => one.id === id);
    if (!next) return;

    setOrganisation(next);
    if (scope === 'record' && list) void navigate({ to: list });
  }

  return (
    <FilterSelect
      hideLabel
      label={LABEL}
      className={className}
      options={options}
      value={value}
      /* A record page renders before its record names an organisation. */
      placeholder='—'
      onValueChange={choose}
    />
  );
}
